import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import "../assets/styles.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { readConsumerOrderSession } from "../orderSession";
import {
  buildConsumerRuntimeHeaders,
  buildConsumerRuntimeOrderPayUrl,
  resolveConsumerRuntimeApiBase,
  resolveConsumerRuntimeConnectionUuid,
} from "../runtime-endpoints";
import { trn, resetLanguage } from "../shared/i18n";
import { refreshBootstrap } from "../runtime-bootstrap";

function buildKioskHeaders(sellingPointId?: string) {
  const headers: Record<string, string> = {
    ...buildConsumerRuntimeHeaders(sellingPointId),
    "Content-Type": "application/json; charset=UTF-8",
    "x-lang": localStorage.getItem("volqan.consumer.language") || "es",
  };
  return headers;
}

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const orderCode = params.get("orderId");
  const printerError = params.get("printerError");
  const mode = params.get("mode");
  const payment = params.get("payment");
  const isTableMode = mode === "table";
  const orderSession = useMemo(() => readConsumerOrderSession(orderCode), [orderCode]);
  const isOnlineQrPayment = payment === "online_qr" || orderSession?.paymentMode === "online_qr";
  const canRequestInvoice = Boolean(!isTableMode && orderSession?.saleId && orderSession?.sellingPointId);
  const [autoRedirect, setAutoRedirect] = useState<boolean>(false);
  const [paymentQrDataUrl, setPaymentQrDataUrl] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<"pending" | "confirmed" | "failed">(
    isOnlineQrPayment ? "pending" : "confirmed"
  );
  const menuRefreshStartedRef = useRef(false);

  useEffect(() => {
    if (paymentStatus !== "confirmed" || menuRefreshStartedRef.current) return;
    const connectionUuid = resolveConsumerRuntimeConnectionUuid();
    if (!connectionUuid || !navigator.onLine) return;

    menuRefreshStartedRef.current = true;
    void refreshBootstrap(connectionUuid, {
      language: localStorage.getItem("volqan.consumer.language") || "es",
      refreshPlatformBranding: false,
    }).catch((error) => {
      // El pedido ya terminó: un fallo de refresco no debe alterar la confirmación.
      console.warn("[order-confirmation] post-order menu refresh failed:", error);
      menuRefreshStartedRef.current = false;
    });
  }, [paymentStatus]);

  const paymentUrl = useMemo(() => {
    if (!isOnlineQrPayment) return "";
    return String(orderSession?.payUrl || buildConsumerRuntimeOrderPayUrl(orderCode)).trim();
  }, [isOnlineQrPayment, orderCode, orderSession?.payUrl]);

  const displayOrderCode = String(orderSession?.friendlyCode || orderCode || "").trim();

  useEffect(() => {
    if (!paymentUrl || !isOnlineQrPayment || paymentStatus === "confirmed") {
      setPaymentQrDataUrl("");
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(paymentUrl, {
      width: 260,
      margin: 1,
      color: {
        dark: "#0f172a",
        light: "#ffffff",
      },
    })
      .then((value: string) => {
        if (!cancelled) setPaymentQrDataUrl(value);
      })
      .catch(() => {
        if (!cancelled) setPaymentQrDataUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [paymentUrl, isOnlineQrPayment, paymentStatus]);

  useEffect(() => {
    if (!isOnlineQrPayment || !orderCode || !orderSession?.sellingPointId) return;

    let cancelled = false;
    let intervalId: number | null = null;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${resolveConsumerRuntimeApiBase(orderSession.sellingPointId)}/orders/${encodeURIComponent(orderCode)}/pay-status`,
          {
            headers: buildKioskHeaders(orderSession.sellingPointId),
          }
        );
        if (cancelled) return;
        const payload = response?.data || {};
        const orderStatus = String(payload?.orderStatus || "").trim().toLowerCase();
        const paymentState = String(payload?.payment?.status || "").trim().toLowerCase();
        if (
          orderStatus === "confirmed" ||
          orderStatus === "completed" ||
          paymentState === "captured" ||
          paymentState === "authorized" ||
          paymentState === "succeeded"
        ) {
          setPaymentStatus("confirmed");
          return;
        }
        if (
          orderStatus === "failed_payment" ||
          orderStatus === "cancelled" ||
          paymentState === "failed" ||
          paymentState === "canceled"
        ) {
          setPaymentStatus("failed");
          return;
        }
        setPaymentStatus("pending");
      } catch {
        if (!cancelled) setPaymentStatus("pending");
      }
    };

    void poll();
    intervalId = window.setInterval(() => {
      void poll();
    }, 4000);

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [isOnlineQrPayment, orderCode, orderSession?.sellingPointId]);

  useEffect(() => {
    if (printerError || (canRequestInvoice && paymentStatus === "confirmed") || paymentStatus !== "confirmed") {
      setAutoRedirect(false);
      return;
    }
    const timer = setTimeout(() => {
      void resetLanguage();
      navigate("/");
    }, 10000);
    setAutoRedirect(true);
    return () => clearTimeout(timer);
  }, [navigate, printerError, canRequestInvoice, paymentStatus]);

  const handleManualConfirm = () => {
    void resetLanguage();
    navigate("/");
  };

  return (
    <div className="fullscreen-container-dark">
      <div className="fullscreen-content d-flex flex-column justify-content-center align-items-center text-center">
        <div
          className={`rounded-circle d-flex justify-content-center align-items-center ${
            paymentStatus === "pending"
              ? "bg-primary"
              : printerError
                ? "bg-warning"
                : paymentStatus === "failed"
                  ? "bg-danger"
                  : "bg-success"
          }`}
          style={{ width: "150px", height: "150px" }}
        >
          <i
            className={`fas fa-${
              paymentStatus === "pending"
                ? "mobile-alt"
                : printerError
                  ? "exclamation-triangle"
                  : paymentStatus === "failed"
                    ? "times"
                    : "check"
            } fa-5x text-white`}
          ></i>
        </div>

        <h1 className="mt-4 display-3 fw-bold">
          {paymentStatus === "pending"
            ? trn("app.consumer.confirmation.scan_to_pay", "Scan to pay")
            : paymentStatus === "failed"
              ? trn("app.consumer.confirmation.payment_failed", "Payment pending")
          : printerError
            ? trn("app.consumer.confirmation.confirmed_but", "Order confirmed, but...")
            : isTableMode
              ? trn("app.consumer.confirmation.sent", "Order sent!")
              : trn("app.consumer.confirmation.confirmed", "Order confirmed!")}
        </h1>

        <p className="fs-3">
          {paymentStatus === "pending"
            ? trn(
                "app.consumer.confirmation.scan_to_pay_body",
                "Scan this QR with your phone to complete the online payment. We will confirm the order here automatically."
              )
            : paymentStatus === "failed"
              ? trn(
                  "app.consumer.confirmation.payment_failed_body",
                  "We could not confirm the online payment yet. Try again from your phone or ask the staff for help."
                )
          : printerError
            ? trn(
                "app.consumer.confirmation.printer_error_body",
                "We could not print your ticket. Please write down your collection code."
              )
            : isTableMode
              ? trn("app.consumer.confirmation.table_body", "We have sent your order to the kitchen for this table.")
              : trn("app.consumer.confirmation.kiosk_body", "Please go to the counter with your ticket.")}
        </p>

        <p className="fs-2 fw-bold text-warning">
          {paymentStatus === "pending"
            ? trn("app.consumer.confirmation.amount_due", "Amount to pay: {amount} {currency}", {
                amount: String(orderSession?.amount?.toFixed?.(2) || orderSession?.amount || "-"),
                currency: orderSession?.currency || "EUR",
              })
            : isTableMode
            ? trn("app.consumer.confirmation.reference", "Reference: {orderCode}", { orderCode: displayOrderCode || "-" })
            : trn("app.consumer.confirmation.code", "Code: {orderCode}", { orderCode: displayOrderCode || "-" })}
        </p>

        {paymentStatus === "pending" && paymentQrDataUrl ? (
          <section className="invoice-cta-panel">
            <div className="invoice-cta-copy">
              <h2>{trn("app.consumer.confirmation.open_mobile", "Open the payment on your phone")}</h2>
              <p>
                {trn(
                  "app.consumer.confirmation.open_mobile_body",
                  "Use your phone camera to scan the QR. It will open the same secure online payment flow used by the public web."
                )}
              </p>
            </div>
            <div className="invoice-cta-grid">
              <div className="invoice-cta-qr">
                <img src={paymentQrDataUrl} alt={trn("app.consumer.confirmation.pay_qr_alt", "Payment QR")} />
                <span>{trn("app.consumer.confirmation.pay_qr_hint", "Scan to pay online")}</span>
              </div>
              <div className="invoice-cta-form">
                <a className="btn btn-warning btn-lg" href={paymentUrl} target="_blank" rel="noreferrer">
                  {trn("app.consumer.confirmation.open_payment_link", "Open payment link")}
                </a>
                <p className="mt-3 mb-0 text-muted">
                  {trn(
                    "app.consumer.confirmation.waiting_payment_poll",
                    "This screen updates automatically when the payment is completed."
                  )}
                </p>
              </div>
            </div>
          </section>
        ) : null}

        {paymentStatus !== "pending" ? (
          <button
            className="btn btn-primary btn-lg mt-4"
            style={{ padding: "15px 30px", fontSize: "1.5rem" }}
            onClick={handleManualConfirm}
          >
            {printerError
              ? trn(
                  "app.consumer.confirmation.noted_code",
                  "I have noted the pickup code",
                  null,
                  "Button on the last kiosk screen. The user taps it to confirm they have written down the order collection code because the ticket could not be printed, and they will use that code to collect the order at the counter."
                )
              : trn("app.consumer.common.ok", "OK")}
          </button>
        ) : null}

        {canRequestInvoice && paymentStatus === "confirmed" ? (
          <div className="mt-3 d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-outline-light btn-lg"
              onClick={() => {
                const nextParams = new URLSearchParams();
                nextParams.set("orderId", String(orderSession?.orderId || orderCode || ""));
                nextParams.set("saleId", String(orderSession?.saleId || ""));
                nextParams.set("sellingPointId", String(orderSession?.sellingPointId || ""));
                navigate(`/invoice-request?${nextParams.toString()}`);
              }}
            >
              {trn("app.consumer.invoice.request_invoice", "Request invoice")}
            </button>
          </div>
        ) : null}

        {!printerError && autoRedirect && paymentStatus === "confirmed" && (
          <p className="mt-3 text-muted">{trn("app.consumer.confirmation.redirecting", "Redirecting automatically...")}</p>
        )}
      </div>
    </div>
  );
};

export default OrderConfirmation;
